import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Lock, Plus, ShieldAlert, Trash2, Users } from 'lucide-react';
import {
  Button,
  Card,
  Field,
  IconButton,
  Pill,
  SectionHeader,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui';
import { useAuth } from '../../contexts';
import { confidentialityApi, confidentialityErrorMessage } from './confidentialityApi';
import { LockMarker } from './LockMarker';
import {
  AccessCandidate,
  AccessSummary,
  CONFIDENTIALITY_BLURB,
  CONFIDENTIALITY_LABELS,
  CONFIDENTIALITY_LEVELS,
  Confidentiality,
  GrantEntityType,
  canSetConfidentiality,
} from './types';

const VIA_LABEL: Record<string, string> = {
  role: 'By role',
  creator: 'Created it',
  grant: 'Given access',
};

const nameOf = (entry: { name: string | null; email: string }) =>
  entry.name?.trim() || entry.email;

/**
 * How closely one record is held, and who can see it.
 *
 * Shown on a record's detail page. Reading it is allowed for anyone who can
 * already see the record — that is the point: the people inside should know
 * who else is, and who is shut out.
 */
export const ConfidentialityPanel: React.FC<{
  entityType: GrantEntityType;
  entityId: string;
  /** Called after a change, so the page can refetch the record itself */
  onChanged?: (summary: AccessSummary) => void;
  className?: string;
}> = ({ entityType, entityId, onChanged, className = '' }) => {
  const { user } = useAuth();
  const [summary, setSummary] = useState<AccessSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [level, setLevel] = useState<Confidentiality>('normal');
  const [note, setNote] = useState('');
  const [editingLevel, setEditingLevel] = useState(false);

  const [adding, setAdding] = useState(false);
  const [candidates, setCandidates] = useState<AccessCandidate[]>([]);
  const [pickedUser, setPickedUser] = useState('');
  const [grantReason, setGrantReason] = useState('');

  const apply = useCallback(
    (next: AccessSummary) => {
      setSummary(next);
      setLevel(next.confidentiality);
      setNote(next.confidentialityNote ?? '');
      onChanged?.(next);
    },
    [onChanged],
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    confidentialityApi
      .access(entityType, entityId)
      .then((next) => {
        if (!alive) return;
        setSummary(next);
        setLevel(next.confidentiality);
        setNote(next.confidentialityNote ?? '');
      })
      .catch((e) => alive && setError(confidentialityErrorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [entityType, entityId]);

  const allowedLevels = useMemo(
    () =>
      CONFIDENTIALITY_LEVELS.filter(
        (value) =>
          value === summary?.confidentiality ||
          canSetConfidentiality(user?.role, summary?.confidentiality ?? 'normal', value),
      ),
    [summary?.confidentiality, user?.role],
  );

  const canChangeLevel =
    !!summary?.canManage &&
    canSetConfidentiality(user?.role, summary.confidentiality, summary.confidentiality);

  const openAdd = async () => {
    setAdding(true);
    setError(null);
    try {
      setCandidates(await confidentialityApi.candidates(entityType, entityId));
    } catch (e) {
      setError(confidentialityErrorMessage(e));
    }
  };

  const saveLevel = async () => {
    setBusy(true);
    setError(null);
    try {
      apply(
        await confidentialityApi.setLevel(entityType, entityId, level, note.trim()),
      );
      setEditingLevel(false);
    } catch (e) {
      setError(confidentialityErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const addPerson = async () => {
    if (!pickedUser) return;
    setBusy(true);
    setError(null);
    try {
      apply(
        await confidentialityApi.grant(
          entityType,
          entityId,
          pickedUser,
          grantReason.trim() || undefined,
        ),
      );
      setAdding(false);
      setPickedUser('');
      setGrantReason('');
    } catch (e) {
      setError(confidentialityErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const removePerson = async (userId: string) => {
    setBusy(true);
    setError(null);
    try {
      apply(await confidentialityApi.revoke(entityType, entityId, userId));
    } catch (e) {
      setError(confidentialityErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card padded className={className}>
        <div className="flex items-center gap-2 text-[13px] text-neutral-500">
          <Spinner /> Loading access
        </div>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card padded className={className}>
        <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error ?? 'Access is unavailable.'}
        </p>
      </Card>
    );
  }

  const grantees = summary.entries.filter((e) => e.via === 'grant');
  const byRank = summary.entries.filter((e) => e.via !== 'grant');

  return (
    <Card className={className}>
      <div className="flex items-start justify-between gap-3 px-4 pt-4">
        <div className="min-w-0">
          <SectionHeader title="Confidentiality" className="!mb-1" />
          <div className="flex flex-wrap items-center gap-2">
            {summary.openToEveryone ? (
              <Pill tone="neutral">
                <Users className="h-3 w-3" strokeWidth={1.75} aria-hidden="true" />
                Normal
              </Pill>
            ) : (
              <LockMarker level={summary.confidentiality} variant="pill" />
            )}
            <span className="text-[12px] text-neutral-500">
              {CONFIDENTIALITY_BLURB[summary.confidentiality]}
            </span>
          </div>
        </div>
        {canChangeLevel && !editingLevel && (
          <Button size="xs" variant="secondary" onClick={() => setEditingLevel(true)}>
            Change
          </Button>
        )}
      </div>

      {summary.confidentialityNote && !editingLevel && (
        <p className="px-4 pt-2 text-[13px] text-ink">{summary.confidentialityNote}</p>
      )}

      {error && (
        <p className="mx-4 mt-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
        </p>
      )}

      {editingLevel && (
        <div className="mt-3 space-y-3 border-t border-neutral-200/70 px-4 py-4">
          <Field
            label="Level"
            htmlFor="conf-level"
            hint={CONFIDENTIALITY_BLURB[level]}
          >
            <Select
              id="conf-level"
              value={level}
              onChange={(e) => setLevel(e.target.value as Confidentiality)}
            >
              {allowedLevels.map((value) => (
                <option key={value} value={value}>
                  {CONFIDENTIALITY_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Reason"
            htmlFor="conf-note"
            hint={level === 'normal' ? 'Cleared when set to normal' : 'Required'}
          >
            <Textarea
              id="conf-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why this is held back"
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setEditingLevel(false);
                setLevel(summary.confidentiality);
                setNote(summary.confidentialityNote ?? '');
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={saveLevel}
              disabled={busy || (level !== 'normal' && !note.trim())}
            >
              Save
            </Button>
          </div>
        </div>
      )}

      <div className="mt-3 border-t border-neutral-200/70">
        <div className="flex items-center justify-between px-4 py-2.5">
          <span className="text-[12px] text-neutral-500">
            {summary.openToEveryone
              ? 'Anyone with a login can see this'
              : `${summary.entries.length} ${
                  summary.entries.length === 1 ? 'person' : 'people'
                } can see this`}
          </span>
          {summary.canManage && !adding && (
            <Button size="xs" variant="ghost" onClick={openAdd}>
              <Plus className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
              Add
            </Button>
          )}
        </div>

        {adding && (
          <div className="space-y-3 border-t border-neutral-200/70 px-4 py-4">
            <Field label="Person" htmlFor="conf-person">
              <Select
                id="conf-person"
                value={pickedUser}
                onChange={(e) => setPickedUser(e.target.value)}
              >
                <option value="">Choose someone</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {nameOf(c)} · {c.email}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Reason" htmlFor="conf-grant-reason" hint="Read at review time">
              <Textarea
                id="conf-grant-reason"
                rows={2}
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                placeholder="Why they need it"
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setAdding(false);
                  setPickedUser('');
                  setGrantReason('');
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={addPerson}
                disabled={busy || !pickedUser}
              >
                Give access
              </Button>
            </div>
          </div>
        )}

        {!summary.openToEveryone && (
          <ul className="divide-y divide-neutral-200/70 border-t border-neutral-200/70">
            {[...grantees, ...byRank].map((entry) => (
              <li
                key={entry.userId}
                className="flex items-center justify-between gap-3 px-4 py-2.5"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[13px] text-ink">
                      {nameOf(entry)}
                    </span>
                    {entry.via === 'grant' && (
                      <Lock
                        className="h-3 w-3 flex-shrink-0 text-neutral-400"
                        strokeWidth={1.75}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="truncate text-[12px] text-neutral-400">
                    {VIA_LABEL[entry.via] ?? entry.via}
                    {entry.reason ? ` · ${entry.reason}` : ''}
                  </div>
                </div>
                {entry.via === 'grant' && summary.canManage && (
                  <IconButton
                    label={`Remove ${nameOf(entry)}`}
                    onClick={() => removePerson(entry.userId)}
                    disabled={busy}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                  </IconButton>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {summary.confidentiality === 'secret' && (
        <p className="flex items-start gap-1.5 border-t border-neutral-200/70 px-4 py-2.5 text-[12px] text-neutral-500">
          <ShieldAlert
            className="mt-px h-3.5 w-3.5 flex-shrink-0 text-primary-600"
            strokeWidth={1.75}
            aria-hidden="true"
          />
          Ordinary admins cannot see this record.
        </p>
      )}
    </Card>
  );
};

export default ConfidentialityPanel;
